import { useEffect, useState } from 'react';
import { getAllLocations, getProfitReport } from '../../api/distributionService';
import { DistributionLocation, ProfitReport } from '../../models/distribution';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate } from '../../utils/formatters';

export default function AdminDashboard() {
  const [locations, setLocations] = useState<DistributionLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [report, setReport] = useState<ProfitReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date()
  });

  const navigate = useNavigate();

      const fetchLocations = async () => {
      try {
        setLoading(true);
        const data = await getAllLocations();
        setLocations(data);
      } catch (err) {
        console.error('Failed to fetch locations', err);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
      console.log('Admin Dashboard component mounted');

    fetchLocations();
  }, []);

  const handleGenerateReport = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    try {
      const data = await getProfitReport(selectedLocation, dateRange.start, dateRange.end);
      setReport(data);
    } catch (err) {
      console.error('Failed to generate report', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (type: 'start' | 'end', value: string) => {
    if (!value) return;
    
    const newDate = new Date(value);
    setDateRange(prev => ({
      ...prev,
      [type]: newDate
    }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
        <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-800">Profit Report</h2>
          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 w-full sm:w-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full">
              <div className="w-full sm:w-64">
                <label className="block text-sm text-gray-500 mb-1">Location</label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full p-2 border rounded text-sm"
                >
                  <option value="">Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 w-full">
                <div className="flex-1 sm:flex-none">
                  <label className="block text-sm text-gray-500 mb-1">From</label>
                  <input
                    type="date"
                    value={dateRange.start.toISOString().split('T')[0]}
                    onChange={(e) => handleDateChange('start', e.target.value)}
                    className="p-2 border rounded text-sm w-full"
                    max={dateRange.end.toISOString().split('T')[0]}
                  />
                </div>
                <div className="flex-1 sm:flex-none">
                  <label className="block text-sm text-gray-500 mb-1">To</label>
                  <input
                    type="date"
                    value={dateRange.end.toISOString().split('T')[0]}
                    onChange={(e) => handleDateChange('end', e.target.value)}
                    className="p-2 border rounded text-sm w-full"
                    min={dateRange.start.toISOString().split('T')[0]}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
            </div>
            <button
              onClick={handleGenerateReport}
              disabled={loading || !selectedLocation}
              className={`px-4 py-2 rounded text-white text-sm font-medium ${
                loading || !selectedLocation
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : 'Generate Report'}
            </button>
          </div>
        </div>

        {report && (
          <div className="p-6 space-y-6">
            {/* Report Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <p className="text-sm font-medium text-blue-600">Gas Purchase</p>
                <p className="text-xl font-bold mt-1">{formatCurrency(report.totalGasPurchase)}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <p className="text-sm font-medium text-blue-600">Gas Sales</p>
                <p className="text-xl font-bold mt-1">{formatCurrency(report.totalGasSales)}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <p className="text-sm font-medium text-green-600">Gas Profit/Loss</p>
                <p className="text-xl font-bold mt-1">{formatCurrency(report.gasProfit)}</p>
              </div>
            </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                <p className="text-sm font-medium text-purple-600">POS Transactions</p>
                <p className="text-xl font-bold mt-1">{formatCurrency(report.totalPOS)}</p>
              </div>
              <div className={`p-4 rounded-lg border ${
                report.profit >= 0 ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
              }`}>
                <p className="text-sm font-medium">
                  {report.profit >= 0 ? 'POS Profit' : 'POSLoss'}
                </p>
                <p className={`text-xl font-bold mt-1 ${
                  report.profit >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(report.posProfit)}
                </p>
              </div>
            </div>

            {/* Report Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Report for {report.locationName} ({formatDate(report.startDate)} to {formatDate(report.endDate)})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-600 mb-2">Location Actions</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => navigate(`/purchase/${report.locationId}`)}
                      className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded text-sm transition-colors"
                    >
                      New Purchase
                    </button>
                    <button
                      onClick={() => navigate(`/purchasereport/${report.locationId}`)}
                      className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded text-sm transition-colors"
                    >
                      Purchase Report
                    </button>
                    <button
                      onClick={() => navigate(`/sales/${report.locationId}`)}
                      className="bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded text-sm transition-colors"
                    >
                      New Sale
                    </button>
                    <button
                      onClick={() => navigate(`/salesreport/${report.locationId}`)}
                      className="bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded text-sm transition-colors"
                    >
                      Sales Report
                    </button>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-600 mb-2">POS Actions</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => navigate(`/pos/${report.locationId}`)}
                      className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-3 rounded text-sm transition-colors"
                    >
                      New POS
                    </button>
                    <button
                      onClick={() => navigate(`/posreport/${report.locationId}`)}
                      className="bg-purple-500 hover:bg-purple-600 text-white py-2 px-3 rounded text-sm transition-colors"
                    >
                      POS Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}